import { useCallback } from 'react';

import { plural } from '../../../../../../shared/helpers';
import { useAddToast } from '../../../../../contexts/ToastContext';

export type AddManyProjectsCounts = {
  added: number;
  skipped: number;
};

export type SetLatestRevisionCounts = {
  updated: number;
  skipped: number;
};

/**
 * Hook which provides functions to display toasts
 * when projects are added or removed.
 */
export default function useBulkActionToasts() {
  const onAddToast = useAddToast();

  //  Function to add a toast when a single project is added
  const addProject = useCallback((projectLocator: string) => {
    onAddToast({
      type: 'info',
      content: `Added project "${projectLocator}".`,
    });
  }, [onAddToast]);

  //  Function to add a toast when multiple projects are added
  const addManyProjects = useCallback(({ added, skipped }: AddManyProjectsCounts) => {
    //  Bail out if both counts are zero
    if ((added + skipped) === 0) {
      return;
    }
    //  Always display the added count, even if it's zero
    let content: string = `Added ${added.toLocaleString()} ${plural(added, 'project')}. `;
    if (skipped > 0) {
      content += `Skipped ${skipped.toLocaleString()} unanalyzed ${plural(skipped, 'project')}. `;
    }
    onAddToast({
      type: 'info',
      content,
    });
  }, [onAddToast]);

  //  Function to add a toast when a single project is removed
  const removeProject = useCallback((projectLocator: string) => {
    onAddToast({
      type: 'info',
      content: `Removed project "${projectLocator}".`,
    });
  }, [onAddToast]);

  //  Function to add a toast when a single project is removed
  const removeManyProjects = useCallback((count: number) => {
    onAddToast({
      type: 'info',
      content: `Removed ${count.toLocaleString()} ${plural(count, 'project')}.`,
    });
  }, [onAddToast]);

  const setLatestRevision = useCallback(({ updated, skipped }: SetLatestRevisionCounts) => {
    let content = `Updated ${updated.toLocaleString()} ${plural(updated, 'project')}. `;

    if (skipped > 0) {
      content += `Skipped ${skipped.toLocaleString()} unanalyzed ${plural(skipped, 'project')}.`;
    }
    onAddToast({
      type: 'info',
      content,
    });
  }, [onAddToast]);

  return {
    addProject,
    addManyProjects,
    removeProject,
    removeManyProjects,
    setLatestRevision,
  };
}
